// idb.ts — the minimal IndexedDB write the push namespace needs to stash the
// service worker's re-subscribe coordinates.
//
// This is the SDK (page-side) counterpart to the reads in
// cmd/edge/backlit-sw.js. Both sides address the SAME database — name
// "backlit-push", object store "kv", version 1 — so the SW's
// pushsubscriptionchange handler can read what the SDK wrote here. The
// name/store/version are passed in by the caller (push.ts owns the constants,
// which are the MUST-MATCH invariant with the SW) rather than hard-coded here,
// keeping this a generic tiny helper.
//
// Deliberately dependency-free and framework-free (matching the SW's own
// vanilla open/onupgradeneeded shape) so the two implementations stay
// obviously equivalent.

// idbPutMany opens (creating on first run) the {store} object store in the
// {dbName}@{version} database and writes every [key, value] pair in a single
// readwrite transaction. Resolves when the transaction commits; rejects on any
// open/transaction error. Values are stored under an out-of-line key (the store
// is created with no keyPath, matching the SW's db.createObjectStore(STORE)).
export function idbPutMany(
  dbName: string,
  store: string,
  version: number,
  entries: Array<[string, unknown]>,
): Promise<void> {
  return openDB(dbName, store, version).then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        let tx: IDBTransaction;
        try {
          tx = db.transaction(store, "readwrite");
        } catch (err) {
          db.close();
          reject(err);
          return;
        }
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error);
        };
        const os = tx.objectStore(store);
        for (const [key, value] of entries) {
          // put(value, key): out-of-line key, mirroring the SW's keyed store.
          os.put(value, key);
        }
      }),
  );
}

// idbGet reads a single key from the {store} object store, resolving the stored
// value or undefined when absent. Mirrors cmd/edge/backlit-sw.js:idbGet so both
// sides read the same shape. Rejects on an open/transaction error.
export function idbGet(dbName: string, store: string, version: number, key: string): Promise<unknown> {
  return openDB(dbName, store, version).then(
    (db) =>
      new Promise<unknown>((resolve, reject) => {
        let tx: IDBTransaction;
        try {
          tx = db.transaction(store, "readonly");
        } catch (err) {
          db.close();
          reject(err);
          return;
        }
        const req = tx.objectStore(store).get(key);
        req.onsuccess = () => {
          resolve(req.result);
          db.close();
        };
        req.onerror = () => {
          reject(req.error);
          db.close();
        };
      }),
  );
}

// openDB opens (and, on first run, creates the {store}) the shared kv database.
// Mirrors cmd/edge/backlit-sw.js:openDB so both sides create an identical store.
function openDB(dbName: string, store: string, version: number): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(dbName, version);
    } catch (err) {
      reject(err);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(store)) {
        db.createObjectStore(store);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("indexedDB open blocked"));
  });
}
